import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Skeleton,
} from '@mui/material';
import {
  TrendingUp,
  AttachMoney,
  Inventory,
  ShowChart,
  Refresh,
  TableChart,
} from '@mui/icons-material';
import { XAxis, YAxis, CartesianGrid, ResponsiveContainer, BarChart, Bar, Legend, LineChart, Line, Tooltip } from 'recharts';
import { getBills } from '../firebase/billingService';
import { getReturns } from '../firebase/returnsService';
import { getInventoryItems } from '../firebase/inventoryService';
import { getBillingSettings } from '../firebase/settingsService';

const Sales = ({ permissions }) => {
  const [bills, setBills] = useState([]);
  const [returns, setReturns] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [companyName, setCompanyName] = useState('Perfume Stories');
  const [refreshing, setRefreshing] = useState(false);
  const [dateRange, setDateRange] = useState({ startDate: '', endDate: '' });
  const [selectedProduct, setSelectedProduct] = useState('all');

  const canViewProfitLoss = permissions?.userType === 'admin' || permissions?.viewProfitLoss === true;

  // Custom tick component for category names with text wrapping
  const CustomYAxisTick = ({ x, y, payload }) => {
    const maxLength = 20;
    const text = payload.value;
    
    if (text.length <= maxLength) {
      return (
        <text x={x} y={y} dy={4} textAnchor="end" fill="#666" fontSize={12}>
          {text}
        </text>
      );
    }
    
    // Split long text into multiple lines
    const words = text.split(' ');
    const lines = [];
    let currentLine = '';
    
    words.forEach(word => {
      if ((currentLine + word).length <= maxLength) {
        currentLine += (currentLine ? ' ' : '') + word;
      } else {
        if (currentLine) lines.push(currentLine);
        currentLine = word;
      }
    });
    if (currentLine) lines.push(currentLine);
    
    return (
      <text x={x} y={y} textAnchor="end" fill="#666" fontSize={11}>
        {lines.map((line, index) => (
          <tspan key={index} x={x} dy={index === 0 ? -((lines.length - 1) * 6) : 12}>
            {line}
          </tspan>
        ))}
      </text>
    );
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    const [billsResult, inventoryResult, returnsResult, settingsResult] = await Promise.all([
      getBills(),
      getInventoryItems(),
      getReturns(),
      getBillingSettings(),
    ]);
    if (billsResult.success) setBills(billsResult.data);
    if (inventoryResult.success) setInventory(inventoryResult.data);
    if (returnsResult.success) setReturns(returnsResult.data);
    if (settingsResult.success && settingsResult.data?.companyName) {
      setCompanyName(settingsResult.data.companyName);
    }
    setLoading(false);
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadData();
    setRefreshing(false);
  };

  // Export to Excel/CSV
  const downloadExcel = () => {
    const filterText = selectedProduct !== 'all' ? `_${selectedProduct}` : '';
    const dateText = dateRange.startDate ? `_${dateRange.startDate}_to_${dateRange.endDate}` : '';
    
    // Create CSV content
    let csv = `${companyName} Analytics Report${filterText}${dateText}\n\n`;
    csv += `Generated: ${new Date().toLocaleString()}\n\n`;
    
    csv += `SUMMARY METRICS\n`;
    csv += `Total Revenue,LKR ${(metrics.totalRevenue || 0).toFixed(2)}\n`;
    csv += `Total Cost,LKR ${(metrics.totalCost || 0).toFixed(2)}\n`;
    csv += `Total Profit,LKR ${(metrics.totalProfit || 0).toFixed(2)}\n`;
    csv += `Profit Margin,${metrics.profitMargin || 0}%\n`;
    csv += `Total Bills,${filteredBills.length}\n`;
    if (metrics.totalKokoCharges > 0) {
      csv += `Koko Service Charges,LKR ${(metrics.totalKokoCharges || 0).toFixed(2)}\n`;
    }
    if (metrics.totalMintPayCharges > 0) {
      csv += `MintPay Service Charges,LKR ${(metrics.totalMintPayCharges || 0).toFixed(2)}\n`;
    }
    csv += `\n`;

    // Product performance
    csv += `\nPRODUCT PERFORMANCE\n`;
    csv += `Product,Units Sold,Revenue\n`;
    salesData.forEach(item => {
      csv += `${item.name || 'Unknown'},${item.unitsSold || 0},LKR ${(item.revenue || 0).toFixed(2)}\n`;
    });

    // Category performance
    const categorySalesData = calculateCategorySales();
    csv += `\nCATEGORY PERFORMANCE\n`;
    csv += `Category,Revenue,Units Sold\n`;
    categorySalesData.forEach(cat => {
      csv += `${cat.category || 'Unknown'},LKR ${(cat.revenue || 0).toFixed(2)},${cat.unitsSold || 0}\n`;
    });

    // Top customers
    const customerAnalytics = calculateCustomerAnalytics();
    csv += `\nTOP 10 CUSTOMERS\n`;
    csv += `Rank,Customer,Total Spent,Orders\n`;
    customerAnalytics.slice(0, 10).forEach((customer, index) => {
      csv += `${index + 1},${customer.name || 'Unknown'},LKR ${(customer.totalSpent || 0).toFixed(2)},${customer.totalOrders || 0}\n`;
    });

    // Retail vs Wholesale
    const pricingData = calculatePricingBreakdown();
    csv += `\nRETAIL VS WHOLESALE\n`;
    csv += `Type,Revenue,Bills,Avg Order Value\n`;
    const retailAvg = pricingData.retail.orders > 0 ? pricingData.retail.revenue / pricingData.retail.orders : 0;
    const wholesaleAvg = pricingData.wholesale.orders > 0 ? pricingData.wholesale.revenue / pricingData.wholesale.orders : 0;
    csv += `Retail,LKR ${(pricingData.retail.revenue || 0).toFixed(2)},${pricingData.retail.orders || 0},LKR ${retailAvg.toFixed(2)}\n`;
    csv += `Wholesale,LKR ${(pricingData.wholesale.revenue || 0).toFixed(2)},${pricingData.wholesale.orders || 0},LKR ${wholesaleAvg.toFixed(2)}\n`;
    if (pricingData.koko.orders > 0) {
      const kokoAvg = pricingData.koko.revenue / pricingData.koko.orders;
      csv += `Koko,LKR ${(pricingData.koko.revenue || 0).toFixed(2)},${pricingData.koko.orders || 0},LKR ${kokoAvg.toFixed(2)}\n`;
      csv += `Koko Service Charges,LKR ${(pricingData.koko.charges || 0).toFixed(2)}\n`;
    }
    if (pricingData.mintpay.orders > 0) {
      const mintPayAvg = pricingData.mintpay.revenue / pricingData.mintpay.orders;
      csv += `MintPay,LKR ${(pricingData.mintpay.revenue || 0).toFixed(2)},${pricingData.mintpay.orders || 0},LKR ${mintPayAvg.toFixed(2)}\n`;
      csv += `MintPay Service Charges,LKR ${(pricingData.mintpay.charges || 0).toFixed(2)}\n`;
    }

    // All transactions
    csv += `\nALL TRANSACTIONS\n`;
    csv += `Date,Customer,Type,Items,Total\n`;
    filteredBills.forEach(bill => {
      csv += `${bill.date || 'N/A'},${bill.customer || 'Unknown'},${bill.customerType || 'N/A'},${bill.items?.length || 0},LKR ${(bill.total || 0).toFixed(2)}\n`;
    });

    // Download
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${companyName.replace(/\s+/g, '_')}_Analytics${filterText}${dateText}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };


  // Filter bills by date range — reactive, no Apply button needed
  const getFilteredBills = () => {
    let filtered = bills;

    if (dateRange.startDate || dateRange.endDate) {
      filtered = filtered.filter(bill => {
        const billDate = new Date(bill.date + 'T00:00:00');
        const start = dateRange.startDate ? new Date(dateRange.startDate + 'T00:00:00') : null;
        const end = dateRange.endDate ? new Date(dateRange.endDate + 'T23:59:59') : null;
        if (start && end) return billDate >= start && billDate <= end;
        if (start) return billDate >= start;
        if (end) return billDate <= end;
        return true;
      });
    }

    if (selectedProduct !== 'all') {
      filtered = filtered.filter(bill =>
        bill.items && bill.items.some(item => item.name === selectedProduct)
      );
    }

    return filtered;
  };

  const filteredBills = getFilteredBills();

  // Filter returns matching the same date range
  const getFilteredReturns = () => {
    let filtered = returns;
    if (dateRange.startDate || dateRange.endDate) {
      filtered = filtered.filter(ret => {
        const retDate = new Date((ret.date || '') + 'T00:00:00');
        const start = dateRange.startDate ? new Date(dateRange.startDate + 'T00:00:00') : null;
        const end = dateRange.endDate ? new Date(dateRange.endDate + 'T23:59:59') : null;
        if (start && end) return retDate >= start && retDate <= end;
        if (start) return retDate >= start;
        if (end) return retDate <= end;
        return true;
      });
    }
    return filtered;
  };
  const filteredReturns = getFilteredReturns();
  const totalReturnsAmt = filteredReturns.reduce((sum, r) => sum + (r.refundAmount || 0), 0);
  const returnedBillIds = new Set(filteredReturns.map(r => r.billId));

  const handleClearDateRange = () => {
    setDateRange({ startDate: '', endDate: '' });
  };

  // Get unique product names for filter dropdown - from ALL bills, not filtered
  const getUniqueProducts = () => {
    const productsMap = new Map();
    // Use ALL bills, not filtered bills, to show all available products
    bills.forEach(bill => {
      if (bill.items) {
        bill.items.forEach(item => {
          if (!productsMap.has(item.name)) {
            // Use category from bill item first
            let category = item.category;
            
            // If no category in bill item, try to look up from inventory
            if (!category || category === 'Uncategorized') {
              const lookupId = item.inventoryId || item.id;
              const inventoryItem = inventory.find(inv => inv.id === lookupId || inv.name === item.name);
              category = inventoryItem?.category || 'Uncategorized';
            }
            
            productsMap.set(item.name, {
              name: item.name,
              category: category,
            });
          }
        });
      }
    });
    return Array.from(productsMap.values()).sort((a, b) => a.name.localeCompare(b.name));
  };

  const uniqueProducts = getUniqueProducts();

  // Calculate sales data from bills
  const calculateSalesData = () => {
    const itemSales = {};
    
    filteredBills.forEach(bill => {
      if (bill.items) {
        bill.items.forEach(item => {
          if (!itemSales[item.name]) {
            itemSales[item.name] = {
              name: item.name,
              unitsSold: 0,
              revenue: 0,
            };
          }
          itemSales[item.name].unitsSold += item.quantity;
          itemSales[item.name].revenue += item.price * item.quantity;
        });
      }
    });

    return Object.values(itemSales).sort((a, b) => b.revenue - a.revenue);
  };

  const salesData = calculateSalesData();

  const calculateSalesMetrics = () => {
    let grossRevenue = 0;
    let totalCost = 0;
    let totalKokoCharges = 0;
    let totalMintPayCharges = 0;
    let totalDeliveryRevenue = 0;

    // Build returned items map for COGS adjustment
    const returnedItemsMap = {};
    filteredReturns.forEach(ret => {
      if (!returnedItemsMap[ret.billId]) returnedItemsMap[ret.billId] = {};
      (ret.items || []).forEach(item => {
        const id = item.inventoryId || item.id;
        returnedItemsMap[ret.billId][id] = (returnedItemsMap[ret.billId][id] || 0) + (item.quantity || 0);
      });
    });

    filteredBills.forEach(bill => {
      // Separate product revenue from delivery revenue
      const billDelivery = bill.paymentType === 'COD' ? (bill.deliveryCharge || 0) : 0;
      const billProductTotal = (bill.total || 0) - billDelivery;

      grossRevenue += billProductTotal;
      totalDeliveryRevenue += billDelivery;
      totalKokoCharges += bill.kokoCharge?.amount || 0;
      totalMintPayCharges += bill.mintPayCharge?.amount || 0;

      // Skip COGS for fully returned bills in same period
      if (returnedBillIds.has(bill.id) && bill.returnStatus === 'Fully Returned') return;

      let billCost = 0;
      if (bill.costOfGoodsSold !== undefined) {
        billCost = bill.costOfGoodsSold || 0;
      } else if (bill.items) {
        bill.items.forEach(billItem => {
          const invId = billItem.inventoryId || billItem.id;
          const inventoryItem = inventory.find(inv => inv.id === invId || inv.name === billItem.name);
          if (inventoryItem) {
            const mlFactor = billItem.isDecant && billItem.decantML ? billItem.decantML : 1;
            billCost += (inventoryItem.averageCost || inventoryItem.costPrice || 0) * mlFactor * billItem.quantity;
          }
        });
      }

      // Subtract COGS of returned items for partial returns
      if (returnedBillIds.has(bill.id) && returnedItemsMap[bill.id]) {
        (bill.items || []).forEach(item => {
          const invId = item.inventoryId || item.id;
          const returnedQty = returnedItemsMap[bill.id][invId] || returnedItemsMap[bill.id][item.id] || 0;
          if (!returnedQty) return;
          const invItem = inventory.find(inv => inv.id === invId || inv.name === item.name);
          if (invItem) {
            const mlFactor = item.isDecant && item.decantML ? item.decantML : 1;
            billCost -= (invItem.averageCost || invItem.costPrice || 0) * mlFactor * returnedQty;
          }
        });
        billCost = Math.max(0, billCost);
      }

      totalCost += billCost;
    });

    // Product revenue only (excludes delivery)
    const productRevenue = grossRevenue - totalReturnsAmt;
    // Gross profit = product revenue - COGS (delivery NOT included)
    const grossProfit = productRevenue - totalCost;
    // Net profit = gross profit + delivery revenue
    const netProfit = grossProfit + totalDeliveryRevenue;
    // Total revenue = product + delivery
    const totalRevenue = productRevenue + totalDeliveryRevenue;
    const profitMargin = productRevenue > 0 ? ((grossProfit / productRevenue) * 100).toFixed(1) : 0;

    // COD stats
    const codBills = filteredBills.filter(b => b.paymentType === 'COD');
    const codOrderCount = codBills.length;
    const avgDeliveryCharge = codOrderCount > 0 ? totalDeliveryRevenue / codOrderCount : 0;

    return {
      totalRevenue, grossRevenue, productRevenue,
      totalDeliveryRevenue, codOrderCount, avgDeliveryCharge,
      totalReturnsAmt, totalCost,
      grossProfit, netProfit,
      totalProfit: netProfit, // backward compat
      profitMargin, totalKokoCharges, totalMintPayCharges
    };
  };

  const metrics = calculateSalesMetrics();

  // Calculate monthly sales data from actual bills
  const calculateMonthlySales = () => {
    const monthlyData = {};
    
    filteredBills.forEach(bill => {
      const date = new Date(bill.date);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const monthName = date.toLocaleString('default', { month: 'short' });
      
      if (!monthlyData[monthKey]) {
        monthlyData[monthKey] = {
          month: monthName,
          year: date.getFullYear(),
          revenue: 0,
          cost: 0,
          income: 0,
        };
      }
      
      // Add product revenue only (exclude delivery charge)
      const billDelivery = bill.paymentType === 'COD' ? (bill.deliveryCharge || 0) : 0;
      monthlyData[monthKey].revenue += (bill.total || 0) - billDelivery;
      
      // Calculate cost — prefer stored FIFO COGS
      if (bill.costOfGoodsSold !== undefined) {
        monthlyData[monthKey].cost += bill.costOfGoodsSold || 0;
      } else if (bill.items) {
        bill.items.forEach(billItem => {
          const invId = billItem.inventoryId || billItem.id;
          const inventoryItem = inventory.find(inv => inv.id === invId || inv.name === billItem.name);
          if (inventoryItem) {
            const mlFactor = billItem.isDecant && billItem.decantML ? billItem.decantML : 1;
            monthlyData[monthKey].cost += (inventoryItem.averageCost || inventoryItem.costPrice || 0) * mlFactor * billItem.quantity;
          }
        });
      }
    });
    
    // Calculate income and sort by date
    const sortedData = Object.entries(monthlyData)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, data]) => ({
        ...data,
        income: data.revenue - data.cost,
      }));
    
    // Get last 6 months or all data if less
    return sortedData.slice(-6);
  };

  const monthlySales = calculateMonthlySales();

  // Calculate category-wise sales
  const calculateCategorySales = () => {
    const categoryData = {};
    
    filteredBills.forEach(bill => {
      if (bill.items) {
        bill.items.forEach(billItem => {
          // Use category from bill item first, then fallback to inventory lookup
          let category = billItem.category;
          let inventoryItem = null;
          
          // If no category in bill item, try to look up from inventory
          if (!category || category === 'Uncategorized') {
            // For decant items, use inventoryId to look up
            const lookupId = billItem.inventoryId || billItem.id;
            inventoryItem = inventory.find(inv => inv.id === lookupId || inv.name === billItem.name);
            category = inventoryItem?.category || 'Uncategorized';
          }
          
          if (!categoryData[category]) {
            categoryData[category] = {
              category,
              revenue: 0,
              cost: 0,
              unitsSold: 0,
            };
          }
          
          categoryData[category].revenue += billItem.price * billItem.quantity;
          categoryData[category].unitsSold += billItem.quantity;
          
          // Look up inventory item for cost if we haven't already
          if (!inventoryItem) {
            const lookupId = billItem.inventoryId || billItem.id;
            inventoryItem = inventory.find(inv => inv.id === lookupId || inv.name === billItem.name);
          }
          
          if (inventoryItem) {
            categoryData[category].cost += inventoryItem.costPrice * billItem.quantity;
          }
        });
      }
    });
    
    return Object.values(categoryData)
      .map(cat => ({
        ...cat,
        profit: cat.revenue - cat.cost,
        margin: cat.revenue > 0 ? ((cat.revenue - cat.cost) / cat.revenue * 100).toFixed(1) : 0,
      }))
      .sort((a, b) => b.revenue - a.revenue);
  };

  const categorySales = calculateCategorySales();

  // Calculate customer analytics
  const calculateCustomerAnalytics = () => {
    const customerData = {};
    
    filteredBills.forEach(bill => {
      if (!customerData[bill.customer]) {
        customerData[bill.customer] = {
          name: bill.customer,
          totalOrders: 0,
          totalSpent: 0,
          customerType: bill.customerType || 'Retail',
        };
      }
      
      customerData[bill.customer].totalOrders += 1;
      customerData[bill.customer].totalSpent += bill.total || 0;
    });
    
    return Object.values(customerData)
      .sort((a, b) => b.totalSpent - a.totalSpent)
      .slice(0, 10); // Top 10 customers
  };

  const topCustomers = calculateCustomerAnalytics();

  // Calculate retail vs wholesale breakdown
  const calculatePricingBreakdown = () => {
    const breakdown = {
      retail: { orders: 0, revenue: 0 },
      wholesale: { orders: 0, revenue: 0 },
      koko: { orders: 0, revenue: 0, charges: 0 },
      mintpay: { orders: 0, revenue: 0, charges: 0 },
    };
    
    filteredBills.forEach(bill => {
      const billDelivery = bill.paymentType === 'COD' ? (bill.deliveryCharge || 0) : 0;
      const billProductTotal = (bill.total || 0) - billDelivery;
      if (bill.paymentType === 'Koko') {
        breakdown.koko.orders += 1;
        breakdown.koko.revenue += billProductTotal;
        breakdown.koko.charges += bill.kokoCharge?.amount || 0;
      } else if (bill.paymentType === 'MintPay') {
        breakdown.mintpay.orders += 1;
        breakdown.mintpay.revenue += billProductTotal;
        breakdown.mintpay.charges += bill.mintPayCharge?.amount || 0;
      } else {
        const type = bill.customerType === 'Wholesale' ? 'wholesale' : 'retail';
        breakdown[type].orders += 1;
        breakdown[type].revenue += billProductTotal;
      }
    });
    
    return breakdown;
  };

  const pricingBreakdown = calculatePricingBreakdown();

  const getProductDetails = (productName) => {
    const sale = salesData.find(s => s.name === productName);
    const item = inventory.find(i => i.name === productName);
    if (!sale || !item) return null;

    // Revenue comes from actual sales (already calculated in salesData)
    const revenue = sale.revenue;
    const cost = item.costPrice * sale.unitsSold;
    const income = revenue - cost;
    const profitMargin = revenue > 0 ? ((income / revenue) * 100).toFixed(1) : 0;

    return { ...sale, ...item, revenue, cost, income, profitMargin };
  };

  const StatCard = ({ icon, value, label, subtitle }) => (
    <Card sx={{ height: '100%', position: 'relative', overflow: 'visible', boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)', border: 'none' }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box
            sx={{
              p: 1.5,
              borderRadius: 2,
              backgroundColor: 'action.hover',
              color: 'text.primary',
              display: 'flex',
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            {icon}
          </Box>
        </Box>
        <Typography variant="h4" fontWeight={700} gutterBottom color="text.primary">
          {value}
        </Typography>
        <Typography variant="body2" color="text.secondary" fontWeight={600}>
          {label}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Box>
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Skeleton variant="text" width={250} height={50} />
            <Skeleton variant="text" width={350} height={30} />
          </Box>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Skeleton variant="rectangular" width={80} height={36} sx={{ borderRadius: 1 }} />
            <Skeleton variant="rectangular" width={100} height={36} sx={{ borderRadius: 1 }} />
          </Box>
        </Box>

        <Card sx={{ mb: 3, boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)', border: 'none' }}>
          <CardContent>
            <Skeleton variant="text" width={200} height={30} sx={{ mb: 2 }} />
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Skeleton variant="rectangular" width={150} height={40} sx={{ borderRadius: 1 }} />
              <Skeleton variant="rectangular" width={150} height={40} sx={{ borderRadius: 1 }} />
              <Skeleton variant="rectangular" width={200} height={40} sx={{ borderRadius: 1 }} />
            </Box>
          </CardContent>
        </Card>

        <Grid container spacing={4} sx={{ mb: 4 }}>
          {[1, 2, 3, 4].map((i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Card sx={{ boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)', border: 'none' }}>
                <CardContent>
                  <Skeleton variant="circular" width={40} height={40} sx={{ mb: 2 }} />
                  <Skeleton variant="text" width="80%" height={40} />
                  <Skeleton variant="text" width="60%" />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Grid container spacing={4}>
          {[1, 2].map((i) => (
            <Grid item xs={12} md={6} key={i}>
              <Card sx={{ boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)', border: 'none' }}>
                <CardContent>
                  <Skeleton variant="text" width={200} height={40} sx={{ mb: 2 }} />
                  <Skeleton variant="rectangular" width="100%" height={300} sx={{ borderRadius: 1 }} />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            Analytics Dashboard
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Track your sales performance and trends
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<TableChart />}
            onClick={downloadExcel}
            color="success"
            size="small"
          >
            Excel
          </Button>
          <Button
            variant="outlined"
            startIcon={refreshing ? <CircularProgress size={20} /> : <Refresh />}
            onClick={handleRefresh}
            disabled={refreshing}
            size="small"
          >
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Refresh</Box>
          </Button>
        </Box>
      </Box>

      {/* Date Range Filter */}
      <Card sx={{ mb: 3, boxShadow: 'none', border: '1px solid', borderColor: 'divider' }}>
        <CardContent sx={{ py: 2 }}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
            Filter Analytics
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1.5 }}>
              <TextField
                label="Start Date"
                type="date"
                size="small"
                value={dateRange.startDate}
                onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: 140 }}
              />
              <TextField
                label="End Date"
                type="date"
                size="small"
                value={dateRange.endDate}
                onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: 140 }}
              />
            </Box>
            
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel>Select Product</InputLabel>
              <Select
                value={selectedProduct}
                label="Select Product"
                onChange={(e) => setSelectedProduct(e.target.value)}
              >
                <MenuItem value="all">All Products</MenuItem>
                {uniqueProducts.map((product) => (
                  <MenuItem key={product.name} value={product.name}>
                    {product.name} ({product.category})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                onClick={() => {
                  handleClearDateRange();
                  setSelectedProduct('all');
                }}
                disabled={!dateRange.startDate && !dateRange.endDate && selectedProduct === 'all'}
              >
                Clear All
              </Button>
            </Box>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
            {(dateRange.startDate || dateRange.endDate) && (
              <Chip
                label={`Date: ${dateRange.startDate || 'Start'} to ${dateRange.endDate || 'End'}`}
                color="primary"
                onDelete={handleClearDateRange}
              />
            )}
            {selectedProduct !== 'all' && (
              <Chip
                label={`Product: ${selectedProduct} (${uniqueProducts.find(p => p.name === selectedProduct)?.category || 'N/A'})`}
                color="secondary"
                onDelete={() => setSelectedProduct('all')}
              />
            )}
            {!dateRange.startDate && !dateRange.endDate && selectedProduct === 'all' && (
              <Chip label="Showing: All Data" color="default" />
            )}
          </Box>
        </CardContent>
      </Card>

      <Grid container spacing={4} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<AttachMoney />}
            value={`LKR ${(metrics.totalRevenue || 0).toLocaleString('en-LK')}`}
            label="Total Revenue"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<Inventory />}
            value={`LKR ${metrics.totalCost.toLocaleString('en-LK')}`}
            label="Total Cost"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<TrendingUp />}
            value={`LKR ${(metrics.netProfit || 0).toLocaleString('en-LK')}`}
            label="Net Income (Profit)"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<ShowChart />}
            value={`${metrics.profitMargin}%`}
            label="Profit Margin"
          />
        </Grid>
      </Grid>

      <Grid container spacing={4} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Card sx={{ boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)', border: 'none' }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Monthly Revenue vs Income
              </Typography>
              {monthlySales.length > 0 ? (
                <Box>
                  <ResponsiveContainer width="100%" height={320}>
                    <BarChart data={monthlySales} margin={{ top: 20, right: 30, left: 0, bottom: 40 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(128,128,128,0.2)" />
                      <XAxis dataKey="month" label={{ value: 'Month', position: 'insideBottomRight', offset: -5 }} />
                      <YAxis tickFormatter={(value) => `${(value / 1000).toFixed(0)}K`} />
                      <Tooltip 
                        formatter={(value) => `LKR ${value.toLocaleString('en-LK', { minimumFractionDigits: 2 })}`}
                        contentStyle={{ 
                          backgroundColor: 'rgba(255, 255, 255, 0.95)', 
                          border: '1px solid #ccc',
                          borderRadius: '4px',
                          padding: '10px'
                        }}
                      />
                      <Legend />
                      <Bar dataKey="revenue" fill="#1976d2" name="Revenue" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="income" fill="#4caf50" name="Net Income" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>
                    Amount (LKR)
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                  <Typography color="text.secondary">No sales data available</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)', border: 'none' }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Income Trend
              </Typography>
              {monthlySales.length > 0 ? (
                <Box>
                  <ResponsiveContainer width="100%" height={320}>
                    <LineChart data={monthlySales} margin={{ top: 20, right: 30, left: 0, bottom: 40 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(128,128,128,0.2)" />
                      <XAxis dataKey="month" label={{ value: 'Month', position: 'insideBottomRight', offset: -5 }} />
                      <YAxis tickFormatter={(value) => `${(value / 1000).toFixed(0)}K`} />
                      <Tooltip 
                        formatter={(value) => `LKR ${value.toLocaleString('en-LK', { minimumFractionDigits: 2 })}`}
                        contentStyle={{ 
                          backgroundColor: 'rgba(255, 255, 255, 0.95)', 
                          border: '1px solid #ccc',
                          borderRadius: '4px',
                          padding: '10px'
                        }}
                      />
                      <Line
                        type="monotone"
                        dataKey="income"
                        stroke="#4caf50"
                        strokeWidth={2.5}
                        dot={{ fill: '#4caf50', r: 5 }}
                        activeDot={{ r: 7 }}
                        name="Net Income"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>
                    Income (LKR)
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                  <Typography color="text.secondary">No sales data available</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={4} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Card sx={{ boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)', border: 'none' }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Sales by Category
              </Typography>
              {categorySales.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={categorySales} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(128,128,128,0.2)" />
                    <XAxis type="number" label={{ value: 'Amount (LKR)', position: 'insideBottomRight', offset: -5 }} />
                    <YAxis 
                      dataKey="category" 
                      type="category" 
                      width={150}
                      tick={<CustomYAxisTick />}
                      interval={0}
                    />
                    <Tooltip 
                      formatter={(value) => `LKR ${value.toLocaleString('en-LK', { minimumFractionDigits: 2 })}`}
                      contentStyle={{ 
                        backgroundColor: 'rgba(255, 255, 255, 0.95)', 
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        padding: '10px'
                      }}
                    />
                    <Legend />
                    <Bar dataKey="revenue" fill="#1976d2" name="Revenue" radius={[0, 4, 4, 0]} />
                    <Bar dataKey="profit" fill="#4caf50" name="Profit" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                  <Typography color="text.secondary">No category data available</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)', border: 'none' }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Top 10 Customers
              </Typography>
              {topCustomers.length > 0 ? (
                <TableContainer sx={{ maxHeight: 300 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Customer</strong></TableCell>
                        <TableCell><strong>Type</strong></TableCell>
                        <TableCell align="right"><strong>Orders</strong></TableCell>
                        <TableCell align="right"><strong>Revenue (LKR)</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {topCustomers.map((customer, index) => (
                        <TableRow key={customer.name} hover>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Chip 
                                label={index + 1} 
                                size="small" 
                                color={index < 3 ? 'primary' : 'default'}
                                sx={{ minWidth: 32 }}
                              />
                              <Typography variant="body2" fontWeight={600}>
                                {customer.name}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={customer.customerType} 
                              size="small" 
                              color={customer.customerType === 'Wholesale' ? 'info' : 'default'}
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell align="right">{customer.totalOrders}</TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" color="success.main" fontWeight={600}>
                              {customer.totalSpent.toLocaleString('en-LK')}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                  <Typography color="text.secondary">No customer data available</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={4} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <Card sx={{ boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)', border: 'none' }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Retail vs Wholesale
              </Typography>              <Box sx={{ mt: 3 }}>
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">Retail Orders</Typography>
                    <Typography variant="body2" fontWeight={600}>{pricingBreakdown.retail.orders}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">Retail Revenue (LKR)</Typography>
                    <Typography variant="body2" fontWeight={600} color="primary.main">
                      {pricingBreakdown.retail.revenue.toLocaleString('en-LK')}
                    </Typography>
                  </Box>
                </Box>
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">Wholesale Orders</Typography>
                    <Typography variant="body2" fontWeight={600}>{pricingBreakdown.wholesale.orders}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">Wholesale Revenue (LKR)</Typography>
                    <Typography variant="body2" fontWeight={600} color="info.main">
                      {pricingBreakdown.wholesale.revenue.toLocaleString('en-LK')}
                    </Typography>
                  </Box>
                </Box>
                {pricingBreakdown.koko.orders > 0 && (
                  <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">Koko Orders</Typography>
                      <Typography variant="body2" fontWeight={600}>{pricingBreakdown.koko.orders}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">Koko Revenue (LKR)</Typography>
                      <Typography variant="body2" fontWeight={600} color="warning.main">
                        {pricingBreakdown.koko.revenue.toLocaleString('en-LK')}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Koko Charges (LKR)</Typography>
                      <Typography variant="body2" fontWeight={600} color="warning.main">
                        {pricingBreakdown.koko.charges.toLocaleString('en-LK')}
                      </Typography>
                    </Box>
                  </Box>
                )}
                {pricingBreakdown.mintpay.orders > 0 && (
                  <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">MintPay Orders</Typography>
                      <Typography variant="body2" fontWeight={600}>{pricingBreakdown.mintpay.orders}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">MintPay Revenue (LKR)</Typography>
                      <Typography variant="body2" fontWeight={600} color="warning.main">
                        {pricingBreakdown.mintpay.revenue.toLocaleString('en-LK')}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">MintPay Charges (LKR)</Typography>
                      <Typography variant="body2" fontWeight={600} color="warning.main">
                        {pricingBreakdown.mintpay.charges.toLocaleString('en-LK')}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={8}>
          <Card sx={{ boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)', border: 'none' }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Category Performance
              </Typography>
              <TableContainer sx={{ maxHeight: 250 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Category</strong></TableCell>
                      <TableCell align="right"><strong>Units Sold</strong></TableCell>
                      <TableCell align="right"><strong>Revenue (LKR)</strong></TableCell>
                      <TableCell align="right"><strong>Profit (LKR)</strong></TableCell>
                      <TableCell align="center"><strong>Margin</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {categorySales.map((cat) => (
                      <TableRow key={cat.category} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {cat.category}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">{cat.unitsSold}</TableCell>
                        <TableCell align="right">
                          {cat.revenue.toLocaleString('en-LK')}
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" color="success.main" fontWeight={600}>
                            {cat.profit.toLocaleString('en-LK')}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={`${cat.margin}%`}
                            size="small"
                            color="success"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* COD Delivery Summary — shown only when there are COD orders with delivery */}
      {metrics.totalDeliveryRevenue > 0 && (
        <Grid container spacing={4} sx={{ mb: 4 }}>
          <Grid item xs={12}>
            <Card sx={{ boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)', border: 'none' }}>
              <CardContent>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  COD Delivery Summary
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>COD Orders</strong></TableCell>
                        <TableCell align="right"><strong>Delivery Revenue (LKR)</strong></TableCell>
                        <TableCell align="right"><strong>Avg Delivery Charge (LKR)</strong></TableCell>
                        <TableCell align="right"><strong>Gross Profit — Products (LKR)</strong></TableCell>
                        <TableCell align="right"><strong>Net Profit incl. Delivery (LKR)</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {metrics.codOrderCount || 0} orders
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight={600} color="info.main">
                            {(metrics.totalDeliveryRevenue || 0).toLocaleString('en-LK', { minimumFractionDigits: 2 })}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight={600}>
                            {(metrics.avgDeliveryCharge || 0).toLocaleString('en-LK', { minimumFractionDigits: 2 })}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight={600} color="success.main">
                            {(metrics.grossProfit || 0).toLocaleString('en-LK', { minimumFractionDigits: 2 })}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Chip
                            label={`LKR ${(metrics.netProfit || 0).toLocaleString('en-LK', { minimumFractionDigits: 2 })}`}
                            size="small"
                            color="success"
                          />
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      <Card sx={{ boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)', border: 'none' }}>
        <CardContent>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            Product Performance Analysis
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Product</strong></TableCell>
                  <TableCell><strong>Category</strong></TableCell>
                  <TableCell align="right"><strong>Units Sold</strong></TableCell>
                  <TableCell align="right"><strong>Revenue (LKR)</strong></TableCell>
                  {canViewProfitLoss && <TableCell align="right"><strong>Cost (LKR)</strong></TableCell>}
                  {canViewProfitLoss && <TableCell align="right"><strong>Net Income (LKR)</strong></TableCell>}
                  {canViewProfitLoss && <TableCell align="center"><strong>Profit Margin</strong></TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {salesData.map((sale) => {
                  const details = getProductDetails(sale.name);
                  if (!details) return null;
                  return (
                    <TableRow key={sale.name} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {details.name}
                        </Typography>
                      </TableCell>
                      <TableCell>{details.category}</TableCell>
                      <TableCell align="right">{details.unitsSold}</TableCell>
                      <TableCell align="right">{details.revenue.toLocaleString('en-LK')}</TableCell>
                      {canViewProfitLoss && (
                        <TableCell align="right">
                          <Typography variant="body2" color="error.main">
                            {details.cost.toLocaleString('en-LK')}
                          </Typography>
                        </TableCell>
                      )}
                      {canViewProfitLoss && (
                        <TableCell align="right">
                          <Typography variant="body2" color="success.main" fontWeight={600}>
                            {details.income.toLocaleString('en-LK')}
                          </Typography>
                        </TableCell>
                      )}
                      {canViewProfitLoss && (
                        <TableCell align="center">
                          <Chip
                            label={`${details.profitMargin}%`}
                            size="small"
                            color="success"
                          />
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Sales;
